import { CategoryPanel } from '@deltek/specification-client'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import {
    DocumentTemplateView,
    DocumentTemplateViewContentItem,
    DocumentTemplateViewItem,
} from '@deltek/specification-client/core/Models/documentTemplate'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectManufacturerProductDetail } from '@deltek/specification-client/core/Models/projectManufacturerProductDetail'
import { ProjectNugget } from '@deltek/specification-client/core/Models/projectNugget'
import { SetProjectContentItemIsHiddenInput } from '@deltek/specification-client/core/Models/projectProductFamily'
import {
    getKpPageItemValues,
    updateKpMainLocalStore,
} from '@deltek/specification-client/core/Utilities/localstorageUtils'
import { SearchFacetInput, SearchFacets } from 'api/graphQL/types/product.type'
import {
    GetNuggetResponse,
    GetProjectNuggetResponse,
    GetProjectNuggetResponseForImportedNugget,
    NuggetResponse,
    ProjectElementNodeWithMetadata,
} from 'api/graphQL/types/projectElement.types'
import { processEnv } from 'components/configuration/environmentVariables'
import { NoteState } from 'models/projectNote'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import { defaultPageSize } from 'pages/ProjectsPage/ProjectsHomePage'
import { NormalizedDocumentTemplateViewItem } from 'pages/ProjectsPage/WorkspacePage/components/Common/model'
import { ElementItemIdentifier } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementTreeView'
import { setNestedObjectPropertyValue } from 'shared/utils/objectUtils'
// SERVICE WORKER RELATED
export const WORKSPACE_PRODUCT_LISTING_KEY = 'workspaceProductListing'
export const WORKSPACE_MANUFACTURER_SELECTED_KEY =
    'workspaceProductTypeManufacturerSelected'
export const WORKSPACE_ENABLED_PTP_OPTIONS = 'workspaceEnabledPTPOptions'
export const WORKSPACE_NO_DROP_CURSOR_ICON =
    'https://avitrucdn.azureedge.net/specpoint/ae/elements/item_no_drop.svg'
export const WORKSPACE_MANUFACTURERS_CONTENT_ITEMS_KEY =
    'workspaceProductTypeManufacturerContentItems'
export const WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY =
    'workspaceProductTypeManufacturerListingItems'
export const WORKSPACE_CATEGORY_CHANGE_KEY = 'workspace_category_change'
export const WORKSPACE_TREE_ELEMENT_NODES = 'workspaceTreeElementNodes'
export const WORKSPACE_PRODUCT_LISTING_ACTION =
    'workspaceProductTListingActions'
export const WORKSPACE_NOTES_PARAMS = 'workspaceNotesParameters'
export const WORKSPACE_NOTE_COUNT_UPDATE = 'workspaceNoteCountUpdate'

export interface WorkspaceProductListing {
    category: CategoryPanel
    projectId: string
    family: string
    productType: string
    familyId?: string
    elementId?: string
    elementType?: string
    nodeId?: string
    elementNode?: ProjectElementNode
    specAgentIdPt?: string
    isLastSubAssembly?: boolean
    assemblyPath?: string
    parentNodeId?: string
    updateBODList?: boolean
    divisionNumber?: string
    selectedFilterType?: string
    categoryNodeId?: string
    baseElementId?: string
    treePath?: string
    isInProject?: boolean
}

export interface WorkspaceTreeElementNodes {
    projectElementNodes: ProjectElementNode[]
    updatedElementId?: string
}

export interface WorkspacePrintPreview {
    projectId: string
    isStudioManagement?: boolean
    elementNode: ProjectElementNode | ProductFamilyStudioManagement
    projectProductFamilyId?: string
    /** Temporary: controls PT preview URL format. Remove this field and the corresponding branch in getPrintPreviewUrl when OM_PT_AUTOMATION is production ready. */
    isOMPTAutomation?: boolean
}
export interface WorkspaceAddSelectedProductCount {
    selectedProducts: number
}

export interface WorkspaceManufacturerSelected {
    contentItems?: Partial<ContentItem>[]
    listingId: string
    isSelected: boolean
    listingText: string
    isBOD: boolean
    removeSelected?: boolean
    deleteBODListing?: boolean
    listingType: ListingLevel
    firmName: string
    productName?: string
    isFirstBOD?: boolean
    specAgentId?: string
    elementId: string
    nodeId?: string // Node ID for the element
    baseElementId?: string // Base element ID for the element
    hasSupportedManufacturersOptions: boolean
    isFromDialogMutation?: boolean // Flag to indicate this message is from a completed mutation
    isBP?: boolean
    isGBP?: boolean
}

// *** this block of code will be enabled while merging changes to new sprint branch to show best practice function , commented only for staging and production
// '#BPtagsRelease' find this to remove all the instance of this check
export const showBPSetting =
    processEnv.REACT_APP_ShowBestPracticesSetting === '1'

export const showSmartProjectCopyChanges =
    processEnv.REACT_APP_EnableSmartProjectCopy === '1'
export interface WorkspaceManufacturerListingItem {
    listingId: string
    isBP: boolean
    isGBP: boolean
}

export interface WorkspaceManufacturerContentItems {
    listingId: string
    contentItems: ContentItem[]
    isForBOD?: boolean
}

export interface HandleContentItemUpdateParams {
    updatedContentItems?: Partial<ContentItem>[]
    projectNuggets?: Partial<ProjectNugget>[]
    retrieveTrackedChanges?: boolean
    overwriteContentItems?: boolean
    contentItemNodesInner?: Partial<ContentItem>[]
    updatedDtv?: Partial<DocumentTemplateView>
    mergeDtv?: boolean
    newIsAddOngoing?: boolean
    newIsAddProhibited?: boolean
}

export interface WorkspaceNotesParameters {
    elementId: string
    contentItemId: string
    noteState?: NoteState
    isAddNote?: boolean
    projectProductFamilyId?: string
    ptNotesProperties?: PTNotesParameters
}

export interface WorkspaceNotesCountUpdate {
    action: 'add' | 'subtract'
}

export interface PTNotesParameters {
    isProductTypeProperty: boolean
}

/** The result of removing a node from the element list, including
 * the updated list and the next node to select.
 */
export interface RemovedElementResult {
    /** The list with the element node removed. */
    elementList: ProjectElementNode[]

    /** The node that should be selected when refreshing the view
     * (typically the next sibling of the node that was removed). */
    nodeToSelect?: ProjectElementNode
}

type TemplateType<T extends boolean> = T extends true
    ? Partial<DocumentTemplateViewItem>
    : NormalizedDocumentTemplateViewItem

// LOCAL STORAGE RELATED
export const localStorageKey = (userId: string) => `ProjectWorkspace-${userId}`
export const localStorageItemKey = (projectId: string) =>
    `workspaceShowHidePanels-${projectId}`

export const updatePersistentConfigs = (
    userId: string = '',
    projectId: string = '',
    propKey: string,
    inputValue: boolean | any
) => {
    if (userId) {
        const saved = getLocalStorageValue(
            localStorageItemKey(projectId),
            userId,
            '',
            localStorageKey(userId)
        )
        const pastItems = saved !== '' ? JSON.parse(saved as string) : {}
        updateKpMainLocalStore({
            currentUserId: userId,
            pageKey: localStorageKey(userId),
            itemKey: localStorageItemKey(projectId),
            itemValue: JSON.stringify(
                setNestedObjectPropertyValue(propKey, inputValue, pastItems)
            ),
        })
    }
}

export const getFromLocalStorage = (
    userId: string,
    projectId: string,
    propKey?: string
) => {
    const saved = getLocalStorageValue(
        localStorageItemKey(projectId ?? ''),
        userId,
        '',
        localStorageKey(userId)
    )
    if (saved === '') {
        return null
    }

    const savedObject = JSON.parse(saved as string)
    return propKey ? savedObject[propKey] : savedObject
}

export const getElementType = (elementType: string) => {
    switch (elementType) {
        case 'ASSEMBLY':
            return 'Assembly'
        case 'PRODUCTFAMILY':
            return 'Family'
        case 'PRODUCTTYPE':
            return 'Product Type'
        default:
            return ''
    }
}
/**
 * get state value of common element properties
 */
export const getStatePUCPropertyValue = (
    statePUCElementProperty: string | undefined
) => {
    if (statePUCElementProperty) {
        switch (statePUCElementProperty.toLowerCase()) {
            case 'new':
                return 'NEW'
            case 'active':
                return 'ACTIVE'
            case 'review needed':
                return 'REVIEWNEEDED'
            case 'in review':
                return 'INREVIEW'
            case 'needs coordination':
                return 'NEEDSCOORDINATION'
            case 'complete':
                return 'COMPLETE'
            default:
                return 'NEW'
        }
    } else return 'NEW'
}

export const escapeElementDelimiter = (plaintext: string): string => {
    return plaintext?.replace(/,/gi, '\\u002c')
}
export const escapeSpecialCharacters = (plaintext: string): string => {
    return plaintext?.replace(/[`|<>{}[]\\\/]/gi, '')
}
export const unescapeElementDelimiter = (escapetext: string): string => {
    return escapetext.replace(/\\u002c/gi, ',')
}
export const getDialogUrl = (
    stateFromSW: Partial<WorkspaceProductListing>,
    type: string
) => {
    let url = `/projects/${stateFromSW.projectId}/${type}?`
    if (
        type === 'projectnotes' &&
        stateFromSW?.elementNode &&
        stateFromSW?.elementNode?.isInProject
    ) {
        if (stateFromSW?.productType) {
            url = `${url}familyId=${stateFromSW?.familyId}&element=${stateFromSW?.elementNode?.elementId}&type=Product Type - ${stateFromSW?.productType}`
        } else if (stateFromSW?.family) {
            url = `${url}familyId=${stateFromSW?.familyId}&element=${
                stateFromSW?.elementNode?.elementId
            }&type=${encodeURIComponent(
                `Family ${escapeElementDelimiter(stateFromSW?.family)}`
            )}`
        } else {
            url = `${url}element=${
                stateFromSW?.elementNode?.elementId
            }&type=${encodeURIComponent(
                `${getElementType(
                    stateFromSW?.elementNode?.elementType
                )} ${escapeElementDelimiter(
                    stateFromSW?.elementNode?.text?.split(/\s/)?.join('||')
                )}`
            )}`
        }
        return url
    } else if (
        type === 'projectnotes' &&
        !(stateFromSW?.elementNode && stateFromSW?.elementNode?.isInProject)
    ) {
        return url.replace('?', '')
    } else {
        if (stateFromSW?.category && stateFromSW?.category?.name !== '') {
            url = `${url}category=${encodeURIComponent(
                escapeElementDelimiter(
                    stateFromSW?.category?.name.split(/\s/).join('||')
                )
            )}`
        }

        if (
            stateFromSW.family &&
            stateFromSW.family !== '' &&
            stateFromSW.family !== 'undefined'
        ) {
            url = `${url}&family=${encodeURIComponent(
                escapeElementDelimiter(
                    stateFromSW.family.split(/\s/).join('||')
                )
            )}`
        }

        if (
            stateFromSW.productType &&
            stateFromSW.productType !== '' &&
            stateFromSW.productType !== 'undefined'
        ) {
            url = `${url}&productType=${encodeURIComponent(
                escapeElementDelimiter(
                    stateFromSW.productType.split(/\s/).join('||')
                )
            )}`
            if (
                stateFromSW.parentNodeId &&
                stateFromSW.parentNodeId !== '' &&
                stateFromSW.parentNodeId !== 'undefined'
            ) {
                url = `${url}&parentNodeId=${stateFromSW.parentNodeId}`
            }
        }

        if (stateFromSW.isLastSubAssembly) {
            url = `${url}&isLastSubAssembly=${stateFromSW.isLastSubAssembly}`
        }

        if (
            stateFromSW.assemblyPath &&
            stateFromSW.assemblyPath !== '' &&
            stateFromSW.assemblyPath !== 'undefined'
        ) {
            url = `${url}&assemblyPath=${encodeURIComponent(
                escapeElementDelimiter(
                    stateFromSW.assemblyPath.split(/\s/).join('#')
                )
            )}`
        }

        if (
            stateFromSW.specAgentIdPt &&
            stateFromSW.specAgentIdPt !== '' &&
            stateFromSW.specAgentIdPt !== 'undefined'
        ) {
            url = `${url}&specAgentIdPt=${stateFromSW.specAgentIdPt}`
        }
    }
    if (stateFromSW?.divisionNumber) {
        url = `${url}&divisionNumber=${stateFromSW.divisionNumber}`
    }
    if (stateFromSW?.selectedFilterType) {
        url = `${url}&selectedFilterType=${stateFromSW.selectedFilterType}`
    }
    if (!!stateFromSW?.categoryNodeId) {
        url = `${url}&categoryNodeId=${stateFromSW.categoryNodeId}`
    }
    url = `${url}&nodeId=${stateFromSW.nodeId}&elementId=${stateFromSW.elementId}&elementType=${stateFromSW.elementType}`

    if (stateFromSW?.baseElementId) {
        url = `${url}&baseElementId=${stateFromSW.baseElementId}`
    }
    if (stateFromSW?.treePath) {
        url = `${url}&treePath=${encodeURIComponent(stateFromSW.treePath)}`
    }
    if (stateFromSW?.isInProject !== undefined) {
        url = `${url}&isInProject=${stateFromSW.isInProject}`
    }

    return url
}

export const getPrintPreviewUrl = (
    stateFromSW: Partial<WorkspacePrintPreview>
) => {
    // Extract and cast element nodes
    const { projectId, isStudioManagement } = stateFromSW
    const projectElementNode = stateFromSW.elementNode as ProjectElementNode
    const sustainabilityElementNode = stateFromSW.elementNode as ProductFamilyStudioManagement

    // Determine element type conditions
    const isSustainabilityTopic =
        sustainabilityElementNode?.type === 'Sustainability Topic'
    const isProductType = projectElementNode?.elementType === 'PRODUCTTYPE'

    // Determine page type (elementLibrary or workspace)
    const pageType = isStudioManagement ? 'elementLibrary' : 'workspace'

    // Determine element ID based on element type
    const elementId =
        isProductType || isSustainabilityTopic
            ? stateFromSW.projectProductFamilyId
            : projectElementNode?.elementId

    // Determine project element node ID
    const projectElementNodeId = isSustainabilityTopic
        ? sustainabilityElementNode?.projectElementNodeId
        : projectElementNode?.id

    // Build base URL
    const baseUrl = `/preview/${projectId}/${pageType}/preview/${
        elementId || projectId
    }`

    // Add additional path segments based on element type
    if (isProductType) {
        // When OM_PT_AUTOMATION is enabled, omit the element's own id segment from the PT preview
        // URL, matching the family-level preview format (parentNodeId only).
        // Remove this branch and the isOMPTAutomation field in WorkspacePrintPreview when production ready.
        if (stateFromSW.isOMPTAutomation) {
            return `${baseUrl}/preview/${projectElementNode?.parentNodeId}`
        }
        return `${baseUrl}/preview/${stateFromSW.elementNode?.id}/preview/${projectElementNode?.parentNodeId}`
    } else {
        return `${baseUrl}/preview/${projectElementNodeId}`
    }
}

export const getLocalStorageValue = (
    field: string,
    currentUserId: string,
    emptyValue: boolean | string | string[],
    localStorageKey: string
) => {
    const workspaceProgressTableStore = getKpPageItemValues({
        currentUserId,
        pageKey: localStorageKey,
    })

    return workspaceProgressTableStore?.[field] ?? emptyValue
}

/**
 * Remove Duplicate ProductTypeFacets for the array
 */
export const removeDuplicateProductTypeFacets = (
    productTypeFacet: Partial<SearchFacets>[]
) => {
    return productTypeFacet.reduce((prev: Partial<SearchFacets>[], current) => {
        const isExist = prev.find(
            (item) =>
                item.value === current.value &&
                item.parentProductFamily === current.parentProductFamily
        )
        if (!isExist) {
            return prev.concat([current])
        } else {
            return prev
        }
    }, [])
}

/**
 * get Selected Product Type Data for comparing productTypeFacets and searchFacetInputs
 */
export const getProductTypeSelectionsData = (
    productTypeFacets: Partial<SearchFacets>[],
    searchFacetInputs: SearchFacetInput[]
) => {
    const selectedProductTypeInputs = searchFacetInputs.find(
        (item) => item.name === 'ProductType'
    )?.values
    const selectedFamilyInputs = searchFacetInputs.find(
        (item) => item.name === 'SectionNames'
    )?.values
    let productTypeSelectionsData: Partial<SearchFacets>[] = []
    let reduceProductTypeFacets = [...productTypeFacets]

    if (selectedProductTypeInputs && selectedFamilyInputs) {
        selectedProductTypeInputs.forEach((value) => {
            const families = reduceProductTypeFacets.filter(
                (pt) => pt.value === value
            )
            const isSelectedFamily = families.find((fam) =>
                selectedFamilyInputs.some(
                    (each) => each.trim() === fam.parentProductFamily?.trim()
                )
            )
            reduceProductTypeFacets = reduceProductTypeFacets.filter((each) => {
                return (
                    each.value !== isSelectedFamily?.value ||
                    each.parentProductFamily !==
                        isSelectedFamily?.parentProductFamily
                )
            })
            productTypeSelectionsData = isSelectedFamily
                ? [...productTypeSelectionsData, ...[isSelectedFamily]]
                : productTypeSelectionsData
        })
    }
    return productTypeSelectionsData
}

/**
 * get index of Project Element Node in the TreePath in AssemblyView
 */
export const getElementIndexInTreePath = (
    elementsData: ProjectElementNode[],
    currentElementNode: ProjectElementNode,
    isDivisionsSelected?: boolean,
    isUpdateAfterVersionChange?: boolean
): number => {
    const data: ProjectElementNode[] =
        isDivisionsSelected && !isUpdateAfterVersionChange
            ? updatedDivisionElements(elementsData)
            : elementsData
    const elementNode: ProjectElementNode =
        isDivisionsSelected && !isUpdateAfterVersionChange
            ? data?.filter(
                  (item) =>
                      item?.baseElementId ===
                          currentElementNode?.baseElementId ||
                      item?.treePath === currentElementNode?.treePath
              )?.[0]
            : currentElementNode
    return data.findIndex((each) => {
        if (
            elementNode?.elementType === 'PRODUCTFAMILY' &&
            each.elementType === 'PRODUCTFAMILY'
        ) {
            if (isDivisionsSelected) {
                return (
                    each?.text === elementNode?.text ||
                    each?.baseElementId === elementNode?.baseElementId
                )
            } else {
                const elementNodeFamilyNunber = elementNode?.text.split('|')[0]
                const treePath = `${elementNode?.parentTreePath}||${elementNodeFamilyNunber}`
                const familyNumber = each.treePath
                    .split('||')
                    ?.pop()
                    ?.split('|')[0]

                return (
                    each.treePath.includes(treePath) &&
                    familyNumber === elementNodeFamilyNunber &&
                    each.parentTreePath === elementNode?.parentTreePath
                )
            }
        }
        if (
            elementNode?.elementType === 'PRODUCTTYPE' &&
            each.elementType === 'PRODUCTTYPE'
        ) {
            if (isDivisionsSelected) {
                return (
                    each?.text === elementNode?.text ||
                    each?.baseElementId === elementNode?.baseElementId
                )
            } else {
                let parentTreePath = elementNode?.parentTreePath.split('||')
                let parentText = parentTreePath.pop()
                let treePath = `${parentTreePath.join('||')}||${
                    parentText?.split('|')[0]
                }`
                return each.parentTreePath.includes(treePath)
            }
        }
        return isDivisionsSelected
            ? each?.text === elementNode?.text ||
                  (!!each?.baseElementId &&
                      !!elementNode?.baseElementId &&
                      each?.baseElementId === elementNode?.baseElementId)
            : each.treePath === elementNode?.treePath
    })
}

/**
 * To check Product Family Name was changed or not, by comparing old and new element nodes
 */
export const checkProductFamilyNameChanged = (
    oldElementList: ProjectElementNode[],
    newElementList: ProjectElementNode[],
    selectedFilterFormat?: string
): boolean => {
    let isProductFamilyNameChanged = false
    const productFamilyList = newElementList.filter(
        (each) => each.elementType === 'PRODUCTFAMILY'
    )
    const oldProductFamilyList = oldElementList.filter(
        (each) => each.elementType === 'PRODUCTFAMILY'
    )
    if (productFamilyList.length > 0 && oldProductFamilyList.length > 0) {
        productFamilyList.forEach((each) => {
            const productFamilyNodeIndex = oldElementList.findIndex(
                (element) => element.treePath === each?.treePath
            )
            const productFamilyNodeIndexIfNameChanged = getElementIndexInTreePath(
                oldElementList,
                each,
                selectedFilterFormat === 'DIVISIONS'
            )
            if (
                productFamilyNodeIndex !== productFamilyNodeIndexIfNameChanged
            ) {
                isProductFamilyNameChanged = true
            }
        })
    }
    return isProductFamilyNameChanged
}

/**
 * replace element by comparing elements with treePath
 */
export const replaceElementsList = (
    data: ProjectElementNode[],
    replaceElementList: ProjectElementNode[],
    isDivisionsSelected?: boolean
): ProjectElementNode[] => {
    if (isDivisionsSelected) {
        updatedDivisionElements(replaceElementList, data).forEach((each) => {
            const updatedElementIndex = updatedDivisionElements(data).findIndex(
                (element) =>
                    element.treePath === each?.treePath ||
                    each?.id === element.id
            )

            if (updatedElementIndex > -1) {
                data[updatedElementIndex] = {
                    ...each,
                    isSearchResult: data[updatedElementIndex].isSearchResult,
                }
            }
        })
    } else {
        replaceElementList.forEach((each) => {
            const updatedElementIndex = data.findIndex(
                (element) =>
                    element.treePath === each?.treePath ||
                    each?.id === element.id
            )
            if (updatedElementIndex > -1) {
                data[updatedElementIndex] = {
                    ...each,
                    isSearchResult: data[updatedElementIndex].isSearchResult,
                }
            }
        })
    }

    return isDivisionsSelected ? updatedDivisionElements(data) : data
}

/**
 * Change ProductTypes with specAgentProductTypeId
 */
export const replaceSpecAgentIdForProductTypes = (
    searchFacetInputs: SearchFacetInput[],
    productTypesData: Partial<SearchFacets>[]
): SearchFacetInput[] => {
    return searchFacetInputs.map((facet) => {
        if (facet.name === 'ProductType') {
            const newValues = facet.values.map((val) => {
                const productType = productTypesData.find(
                    (item) => item.value === val
                )
                return productType?.specAgentProductTypeId ?? ''
            })
            return {
                name: 'specAgentProductTypeId',
                values: newValues,
            }
        }
        return facet
    })
}

/**
 * return Assembly Path By Element
 */
export const getAssemblyPathByElement = (
    element: ProjectElementNode
): string => {
    // ASSEMBLY
    if (element?.elementType === 'ASSEMBLY') {
        return element.treePath
    }
    // PRODUCTFAMILY
    if (element?.elementType === 'PRODUCTFAMILY') {
        return element.parentTreePath
    }
    // PRODUCTTYPE
    if (element?.elementType === 'PRODUCTTYPE') {
        const treePath = element.parentTreePath.split('||')
        treePath.pop()
        return treePath.join('||')
    }
    return ''
}

export const checkSiblingsHaveBestPractices = (
    list: ProjectElementNode[],
    path: string,
    key: 'isBestPractice' | 'isGlobalBestPractice'
): boolean => {
    const filteredList = list.filter(
        (each) => each.parentTreePath === path && each[key]
    )
    return filteredList && filteredList.length > 0
}

export const isElementHasChildrenById = (
    id: string,
    list: ProjectElementNode[]
): boolean => {
    const element = list.find((each) => each.id === id)
    return element?.hasChildren ?? false
}
export const getDivisionNumberOfSelectedDivision = (
    selectedNode: ProjectElementNode,
    selectedElementFormat: string
): string | undefined => {
    // If the selected node is an assembly, get division from its treePath
    if (
        selectedNode?.elementType === 'ASSEMBLY' &&
        selectedElementFormat === 'DIVISIONS'
    ) {
        return selectedNode.treePath?.split('|')?.[0]
    }

    if (selectedElementFormat !== 'DIVISIONS' || !selectedNode) {
        return undefined
    }

    // For non-assembly elements in division view, get division from treePath or parentTreePath
    // In division view, the treePath structure is: "Division|Assembly||Family" or "Division|Assembly||Family||ProductType"
    if (selectedNode.elementType === 'PRODUCTFAMILY') {
        // For family: treePath is "Division|Assembly||Family", so get first part before ||
        return selectedNode.treePath?.split('||')?.[0]?.split('|')?.[0]
    }

    if (selectedNode.elementType === 'PRODUCTTYPE') {
        // For product type: treePath is "Division|Assembly||Family||ProductType", so get first part before ||
        return selectedNode.treePath?.split('||')?.[0]?.split('|')?.[0]
    }

    return undefined
}
/**
 * Returns the result by updated isBestPractice or isGlobalBestPractice element nodes maped with treePath inlcuding parents.
 */
export const getUpdateBpElementListByTreePath = (
    elementList: ProjectElementNode[],
    childElementList: ProjectElementNode[],
    treepath: string,
    isBP: boolean,
    isGBP: boolean,
    remove: boolean,
    isParent?: boolean
): ProjectElementNode[] => {
    const selectElementByTreePath = elementList.find(
        (each) => each.treePath === treepath
    )
    const isChildrenHaveBP = checkSiblingsHaveBestPractices(
        [...elementList, ...childElementList],
        treepath,
        'isBestPractice'
    )
    const isChildrenHaveGBP = checkSiblingsHaveBestPractices(
        [...elementList, ...childElementList],
        treepath,
        'isGlobalBestPractice'
    )
    const parentTreePath = selectElementByTreePath?.parentTreePath
    if (showSmartProjectCopyChanges) {
        let newElementList = getUpdatedBpElementList(
            elementList,
            treepath,
            isBP,
            isGBP,
            remove,
            !!isParent ? isParent : false,
            false,
            false
        )
        return newElementList
    } else {
        let newElementList = getUpdatedBpElementList(
            elementList,
            treepath,
            isBP,
            isGBP,
            remove,
            !!isParent ? isParent : false,
            isChildrenHaveBP,
            isChildrenHaveGBP
        )
        if (parentTreePath && parentTreePath !== '') {
            const isSiblingsHaveBP = checkSiblingsHaveBestPractices(
                newElementList,
                parentTreePath,
                'isBestPractice'
            )
            const isSiblingsHaveGBP = checkSiblingsHaveBestPractices(
                newElementList,
                parentTreePath,
                'isGlobalBestPractice'
            )
            const isParentBP = !!isSiblingsHaveBP ? true : isBP
            const isParentGBP = !!isSiblingsHaveBP
                ? false
                : !!isSiblingsHaveGBP
                ? true
                : isGBP
            newElementList = getUpdateBpElementListByTreePath(
                newElementList,
                [] as ProjectElementNode[],
                parentTreePath,
                isParentBP,
                isParentBP ? false : isParentGBP,
                false,
                true
            )
        }
        return newElementList
    }
}

/**
 * Returns the result by updated isBestPractice or isGlobalBestPractice match with treePath.
 */
export const getUpdatedBpElementList = (
    elementList: ProjectElementNode[],
    treepath: string,
    isBP: boolean,
    isGBP: boolean,
    remove: boolean,
    isParent: boolean,
    isChildrenHaveBP: boolean,
    isChildrenHaveGBP: boolean
): ProjectElementNode[] => {
    if (showSmartProjectCopyChanges) {
        return elementList.map((item) => {
            return item.treePath === treepath
                ? item.hasBestPractice && isParent
                    ? item
                    : {
                          ...item,
                          isBestPractice: remove ? false : isBP,
                          isGlobalBestPractice: remove ? false : isGBP,
                          hasBestPractice: remove
                              ? false
                              : isParent
                              ? false
                              : isBP || isGBP,
                      }
                : item
        })
    } else {
        return elementList.map((item) => {
            return item.treePath === treepath
                ? item.hasBestPractice && isParent
                    ? item
                    : {
                          ...item,
                          isBestPractice:
                              remove && isChildrenHaveBP
                                  ? true
                                  : remove
                                  ? false
                                  : isBP,
                          isGlobalBestPractice:
                              remove && isChildrenHaveBP
                                  ? false
                                  : remove && isChildrenHaveGBP
                                  ? true
                                  : remove
                                  ? false
                                  : isGBP,
                          hasBestPractice: remove
                              ? false
                              : isParent
                              ? false
                              : isBP || isGBP,
                      }
                : item
        })
    }
}

/**
 * Returns the result of inserting the given product type element node into the given element list.
 * The original list is not modified.
 */
export const insertProductTypeElement = (
    /**
     * The product type element to insert.
     */
    elementToInsert: ProjectElementNode,

    /**
     * The element list to insert the product type into. (This array isn't modified; a new array is returned.)
     * Should contain at least one sibling product type. Siblings are assumed to be contiguous in the array.
     */
    elementList: ProjectElementNode[],
    /*
     * To indicate if adding elements from additional instances
     */
    isAdditionalInstances?: boolean
): ProjectElementNode[] => {
    // Get the new node's sibling elements
    const parentId = elementToInsert.parentNodeId
    const siblings: ProjectElementNode[] = elementList.filter(
        (element) => element.parentNodeId === parentId
    )
    const siblingCount = siblings.length
    if (siblingCount === 0) {
        if (isAdditionalInstances) {
            const familyNode = {
                ...elementList[elementList?.length - 1],
                hasChildren: true,
            }
            return [
                ...elementList.slice(0, elementList?.length - 1),
                familyNode,
                elementToInsert,
            ]
        }
        return [...elementList]
    }
    // Remember where first sibling is in the element list,
    // so we can replace the range of sibling nodes in the list later
    const indexOfFirstSibling = elementList.indexOf(siblings[0])

    // Increment the orders of the siblings that will come after the new node
    let updatedSiblings: ProjectElementNode[] = siblings.map((sib) => ({
        ...sib,
        nodeOrder:
            sib.nodeOrder !== undefined &&
            elementToInsert.nodeOrder !== undefined &&
            sib.nodeOrder >= elementToInsert.nodeOrder
                ? sib.nodeOrder + 1
                : sib.nodeOrder,
    }))

    // Insert the new node into the updated sibling list in the right place
    // according to its order
    const indexOfFirstSiblingWithGreaterOrder = updatedSiblings.findIndex(
        (sib) =>
            sib.nodeOrder !== undefined &&
            elementToInsert.nodeOrder !== undefined &&
            sib.nodeOrder > elementToInsert.nodeOrder
    )

    const insertIndex =
        indexOfFirstSiblingWithGreaterOrder === -1
            ? updatedSiblings.length
            : indexOfFirstSiblingWithGreaterOrder

    // Separate Manufacturer Product Types
    const { msElementsList, moElementsList } = updatedSiblings.reduce(
        (acc, curr) => {
            if (curr.isBasedFromManufacturerOption) {
                acc.moElementsList.push(curr)
            } else {
                acc.msElementsList.push(curr)
            }
            return acc
        },
        {
            msElementsList: [] as ProjectElementNode[],
            moElementsList: [] as ProjectElementNode[],
        }
    )

    if (!elementToInsert.isBasedFromManufacturerOption) {
        updatedSiblings = [
            ...msElementsList.slice(0, insertIndex),
            elementToInsert,
            ...msElementsList.slice(insertIndex),
            ...moElementsList,
        ]
    } else {
        const sortedMO = [...moElementsList, elementToInsert].sort((a, b) => {
            if (a.text.toLowerCase() < b.text.toLowerCase()) {
                return -1
            } else if (a.text.toLowerCase() > b.text.toLowerCase()) {
                return 1
            }
            return 0
        })
        updatedSiblings = [...msElementsList, ...sortedMO]
    }

    // Return a new element list with the updated siblings
    return [
        ...elementList.slice(0, indexOfFirstSibling),
        ...updatedSiblings,
        ...elementList.slice(indexOfFirstSibling + siblingCount),
    ]
}

/**
 * Returns the given element list with the given element node removed.
 * The original list is not unmodified.
 */
export const removeElementNodeFromList = (
    /**
     * The node to remove.
     */
    element: ProjectElementNode,

    /**
     * The element list to remove the node from. (This array isn't modified; a new array is returned.)
     */
    elementListData: ProjectElementNode[],
    isDivisionsSelected?: boolean
): RemovedElementResult => {
    const elementList = isDivisionsSelected
        ? updatedDivisionElements(elementListData)
        : elementListData
    // Get the node's sibling elements
    const parentId = element.parentNodeId
    const siblings = elementList.filter(
        (node) => node.parentNodeId === parentId
    )
    const siblingCount = siblings.length

    // Remember where first sibling is in the element list,
    // so we can replace the range of sibling nodes in the list later
    const indexOfFirstSibling = elementList.indexOf(siblings[0])

    // Determine the next element that should be selected in the tree
    let nodeToSelect: ProjectElementNode | undefined
    if (siblingCount <= 1) {
        // Element has no siblings; the parent should be the next selected.
        nodeToSelect = elementList.find((node) => node.id === parentId)
        if (indexOfFirstSibling > 0) {
            elementList[indexOfFirstSibling - 1].hasChildren = false
        }
    } else {
        // Node's next sibling should be selected, or its previous sibling if there's no next sibling
        const nodeIndexInSiblings = siblings.findIndex(
            (node) => node.id === element.id
        )
        nodeToSelect =
            nodeIndexInSiblings === siblingCount - 1
                ? siblings[siblingCount - 2] // previous sibling
                : siblings[nodeIndexInSiblings + 1] // next sibling
    }

    // Decrement the orders of the siblings that come after the node we're removing
    let updatedSiblings: ProjectElementNode[] = siblings.map((sib) => ({
        ...sib,
        nodeOrder:
            sib.nodeOrder !== undefined &&
            element.nodeOrder !== undefined &&
            sib.nodeOrder >= element.nodeOrder
                ? sib.nodeOrder - 1
                : sib.nodeOrder,
    }))

    // Remove node from the updated sibling list
    const indexOfElement = updatedSiblings.findIndex(
        (node) => node.id === element.id
    )
    if (indexOfElement !== -1) {
        updatedSiblings = updatedSiblings
            .slice(0, indexOfElement)
            .concat(updatedSiblings.slice(indexOfElement + 1))
    }

    // Return a new element list with the siblings replaced
    return {
        elementList: [
            ...elementList.slice(0, indexOfFirstSibling),
            ...updatedSiblings,
            ...elementList.slice(indexOfFirstSibling + siblingCount),
        ],
        nodeToSelect: nodeToSelect,
    }
}

/**
 * Returns a copy of the given elements, but with their treePath and parentTreePath set for Divisions view.
 * @param elements The elements in Assembly format
 * @param fallbackElements Fallback list to check for parent elements if they are not in {@link elements}.
 * Be sure to provide this if {@link elements} does not contain all the parent elements (e.g., when it is filtered by element type).
 * @returns Copy of the given elements with treePath and parentTreePath set for Divisions view
 */
export const updatedDivisionElements = (
    elements: ProjectElementNode[],
    fallbackElements?: ProjectElementNode[]
) => {
    // Create maps of IDs to nodes for efficient lookups inside the map function
    const elementMap = new Map<string, ProjectElementNode>()
    elements?.forEach((item) => {
        elementMap.set(item.id, item)
    })

    const fallbackElementMap = new Map<string, ProjectElementNode>()
    fallbackElements?.forEach((item) => {
        fallbackElementMap.set(item.id, item)
    })

    // Map onto a new array of elements with updated treePath and parentTreePath for Divisions view
    const updatedElementData = elements?.map((item: ProjectElementNode) => {
        const parentItem = item.parentNodeId
            ? elementMap.get(item.parentNodeId) ||
              fallbackElementMap.get(item.parentNodeId)
            : undefined
        const grandParentItem = parentItem?.parentNodeId
            ? elementMap.get(parentItem.parentNodeId) ||
              fallbackElementMap.get(parentItem.parentNodeId)
            : undefined

        const currentItemTreePath =
            item.elementType === 'PRODUCTFAMILY'
                ? `${parentItem?.text}||${item.text}`
                : `${grandParentItem?.text}||${parentItem?.text}||${item.text}`

        return {
            ...item,
            treePath:
                item.elementType === 'ASSEMBLY'
                    ? `${item.text}`
                    : currentItemTreePath,

            parentTreePath:
                item.elementType === 'ASSEMBLY'
                    ? ''
                    : (item.elementType === 'PRODUCTFAMILY'
                          ? parentItem?.text
                          : `${grandParentItem?.text}||${parentItem?.text}`) ??
                      '',
        }
    })
    return updatedElementData
}

/**
 * Walks up the parentNodeId chain for every in-project node and marks each
 * ancestor as isInProject: true.  Used in Division view where the server only
 * sets isInProject on leaf PT nodes — ancestor Section/Division nodes must be
 * promoted client-side so the green highlight bubbles up the tree.
 */
export const propagateIsInProjectToAncestors = (
    elementList: ProjectElementNode[]
): ProjectElementNode[] => {
    const inProjectNodes = elementList.filter((n) => n.isInProject)
    if (inProjectNodes.length === 0) return elementList

    const nodeById = new Map<string, ProjectElementNode>()
    elementList.forEach((n) => nodeById.set(n.id, n))

    const ancestorIdsToUpdate = new Set<string>()
    for (const node of inProjectNodes) {
        let parentId = node.parentNodeId
        while (parentId) {
            const ancestor = nodeById.get(parentId)
            if (!ancestor) break
            if (!ancestor.isInProject) {
                ancestorIdsToUpdate.add(ancestor.id)
            }
            parentId = ancestor.parentNodeId
        }
    }

    if (ancestorIdsToUpdate.size === 0) return elementList

    return elementList.map((n) =>
        ancestorIdsToUpdate.has(n.id) ? { ...n, isInProject: true } : n
    )
}

export const filteredProductList = (
    data: Partial<GetProjectNuggetResponseForImportedNugget>
) => {
    return data?.getProjectNugget?.productListings?.filter(
        (item: Partial<ProjectManufacturerProductDetail>) => {
            return item.submissionStatus?.toUpperCase() !== 'DEPRECATED'
        }
    )
}

export const mergeAndSortProducts = (
    productListings?: Partial<ProjectManufacturerProductDetail>[],
    customManufacturers?: Partial<ProjectManufacturerProductDetail>[]
): Partial<ProjectManufacturerProductDetail>[] => {
    // merge into a single array of manufacturers
    const merged = (productListings ?? []).concat(customManufacturers ?? [])

    // Remove duplicates based on customManufacturerProductId or listingText
    const deduped = merged.filter((item, index, self) => {
        return index === self.findIndex((t) => {
            // Use customManufacturerProductId if available, otherwise use listingText
            if (item.customManufacturerProductId && t.customManufacturerProductId) {
                return t.customManufacturerProductId === item.customManufacturerProductId
            }
            return t.listingText === item.listingText
        })
    })

    // sort them by listingText (displayed in UI)
    // Match C# ordinal sorting: compare strings character by character using Unicode values
    // This ensures numbers come first (e.g., "1-800-Bollards" before "Carlisle")
    const sorted = deduped.sort((a, b) => {
        const aText = a.listingText || ''
        const bText = b.listingText || ''

        // Use localeCompare with numeric and case sensitivity for proper sorting
        return aText.localeCompare(bText, 'en', {
            numeric: true,
            sensitivity: 'variant' // Preserves case differences like C# ordinal comparison
        })
    })

    return sorted
}

export const isPropertyKeyEditable = (
    selectedNode?: ProjectElementNode,
    template?: NormalizedDocumentTemplateViewItem,
    contentTeamFromStudioPage?: boolean
): boolean => {
    // allow user to edit `userAdded` or `sustainability topic`
    // the partType of sustainability topic is assigned as `ContentGroup`
    if (template?.isUserAdded || template?.partType === 'ContentGroup')
        return true
    else if (
        selectedNode?.isImportedToProject ||
        selectedNode?.isBpPublished ||
        contentTeamFromStudioPage
    ) {
        if (selectedNode?.elementType === 'PRODUCTFAMILY') {
            return true
        } else if (selectedNode?.elementType === 'PRODUCTTYPE') {
            // PT keys remain non-editable - warning shows on click
            if (template?.isProductType) return false
            // PBI 2407205: Allow base content keys to be edited
            // Warning with Cancel/Proceed will show on blur for base content keys
            return true
        }
    }
    return false
}

export const getArticles = (
    contentItemNodes?: Partial<ContentItem>[],
    dtv?: Partial<DocumentTemplateView> | null,
    partType?: string,
    isSMMWorkspace?: boolean
) => {
    const allArticlesContentItemNodes = contentItemNodes?.filter(
        (item) => item?.textType === 'Article'
    )
    const allArticlesDTV = dtv?.items
        ?.map((item) => {
            const dtvItem = item?.documentTemplateViewContents?.[0]?.documentTemplateViewContentItems?.filter(
                (itemIn) =>
                    allArticlesContentItemNodes?.some(
                        (itemarticles) => itemarticles.id === itemIn.id
                    )
            )?.[0]

            if (dtvItem) {
                return { ...dtvItem, partType: item?.partType }
            }
        })
        ?.filter((item) => item)
    return (allArticlesContentItemNodes as Partial<
        NormalizedDocumentTemplateViewItem
    >[])
        ?.filter((item) => {
            const itemContent = allArticlesDTV?.filter?.(
                (itemDtv) => itemDtv?.id === item?.id
            )?.[0]
            return (
                (isSMMWorkspace || !itemContent?.isHidden) &&
                itemContent?.partType === partType
            )
        })
        ?.map((item) => {
            return {
                ...item,
                paragraphOrder: allArticlesDTV?.filter(
                    (itemDTV) => itemDTV?.id === item?.id
                )?.[0]?.paragraphOrder,
            }
        })
        ?.sort((a, b) => a && b && a?.paragraphOrder! - b?.paragraphOrder!)
}

/**
 * Retrieves the given element from a list of project element nodes based on the provided
 * element identifier and filter type.
 *
 * @param nodes - The array of `ProjectElementNode` objects from which to get the identified node, or undefined.
 * @param selectedElementIdentifier - An object containing the identifier of the element to get.
 * @param elementFilterType - A string representing the type of filter to apply (e.g., 'DIVISIONS').
 * @returns The selected `ProjectElementNode` if found, otherwise undefined.
 */
export const getNodeByItemIdentifier = (
    nodes: ProjectElementNode[] | ProjectElementNodeWithMetadata[] | undefined,
    selectedElementIdentifier: ElementItemIdentifier | undefined,
    elementFilterType: string | undefined
): ProjectElementNode | ProjectElementNodeWithMetadata | undefined => {
    // In Divisions view, there should be only one instance of a given element ID in the tree;
    // otherwise, we should take the treepath into account since there may be multiple instances
    // of a given element ID under different assemblies.
    return nodes?.find((node) => {
        if (
            elementFilterType === 'DIVISIONS'
                ? node.id === selectedElementIdentifier?.id ||
                  node?.elementId === selectedElementIdentifier?.id ||
                  node.treePath === selectedElementIdentifier?.treePath // we may have just added/removed node so it may have different ID now
                : node.treePath === selectedElementIdentifier?.treePath
        ) {
            return node
        }
    })
}

/**
 * Updates the hidden status of a content item in a project or document template view by calling handleInActivateProperty function.
 *
 * @param template - The template from which to get the documentTemplateViewId and contentItemOwnerId.
 * @param selectedProjectNode - The selected project node to get the nodeId.
 * @param familyAddedProductTypes - The family added product types to find the product type node of the template.
 * @param projectId - The ID of the project.
 * @param handleInActivateProperty - The callback function to handle the update of the content item's hidden status.
 * @param nugget - The nugget object to get the projectNuggetId.
 * @param hasTitle - A flag indicating whether the template has a title.
 * @param projectProductTypeProductListingsInfo - The project product type product listings info to get the projectNuggetId.
 * @param dtv - The document template view object to get the documentTemplateViewId.
 * @param isFromWorkSpace - A flag indicating whether the update is from the workspace.
 */
export const updateContentItemHiddenStatus = <T extends boolean>(
    template: TemplateType<T> | undefined,
    selectedProjectNode: ProjectElementNode | null,
    familyAddedProductTypes: ProjectElementNode[] | undefined,
    projectId: string,
    handleInActivateProperty?: (
        SetProjectContentItemIsHiddenInput: Partial<
            SetProjectContentItemIsHiddenInput
        >
    ) => void,
    nugget?: NuggetResponse,
    hasTitle?: boolean,
    projectProductTypeProductListingsInfo?:
        | Partial<GetProjectNuggetResponse>
        | Partial<GetNuggetResponse>,
    dtv?: Partial<DocumentTemplateView> | null,
    isFromWorkSpace?: T,
    isOMWorkspace?: T
): void => {
    try {
        // Check if the template is defined
        if (!template) {
            throw new Error('Template is undefined.')
        }
        // Check if the selected node is a family
        const isFamily = selectedProjectNode?.elementType === 'PRODUCTFAMILY'

        // Helper to get the documentTemplateViewContentItem
        const getDocumentTemplateViewContentItem = (
            template: TemplateType<T>
        ): Partial<DocumentTemplateViewContentItem> | undefined => {
            if (isFromWorkSpace) {
                return (
                    template?.documentTemplateViewContents?.[0]
                        ?.documentTemplateViewContentItems?.[0] ??
                    template?.documentTemplateViewContents?.[0]?.documentTemplateViewContentItems?.find(
                        (
                            dtvContentItem: Partial<
                                DocumentTemplateViewContentItem
                            >
                        ) =>
                            dtvContentItem.id ===
                            template?.documentTemplateViewContents?.[0]
                                .contentId
                    )
                )
            } else {
                return (
                    template?.documentTemplateViewContents?.[0]
                        ?.documentTemplateViewContentItems?.[0] ??
                    (template as NormalizedDocumentTemplateViewItem)?.documentTemplateViewItem?.documentTemplateViewContents?.[0]?.documentTemplateViewContentItems?.find(
                        (
                            dtvContentItem: Partial<
                                DocumentTemplateViewContentItem
                            >
                        ) =>
                            dtvContentItem.id ===
                            (template as NormalizedDocumentTemplateViewItem)
                                ?.contentItem?.id
                    )
                )
            }
        }

        // Helper to determine the projectNuggetId and nodeId
        const getNodeAndNuggetIds = (
            template: TemplateType<T>,
            selectedProjectNode: ProjectElementNode | null,
            familyAddedProductTypes: ProjectElementNode[] | undefined,
            nugget?: NuggetResponse | undefined,
            projectProductTypeProductListingsInfo?:
                | Partial<GetProjectNuggetResponse>
                | Partial<GetNuggetResponse>
                | undefined,
            isFamily?: boolean
        ): {
            nodeId: string | undefined
            projectNuggetId: string | undefined
        } => {
            let projectNuggetId: string | undefined = undefined
            let nodeId = selectedProjectNode?.id

            if (template?.isManufacturers || template?.isBOD) {
                if (isFamily && template?.partType === 'Products') {
                    const productTypeNodeOfTemplate = familyAddedProductTypes?.find(
                        (x) => x.baseElementId === template.parentId
                    )
                    nodeId = productTypeNodeOfTemplate?.id
                    projectNuggetId =
                        productTypeNodeOfTemplate?.elementId || undefined
                } else {
                    projectNuggetId =
                        (isFromWorkSpace && selectedProjectNode?.elementId
                            ? (projectProductTypeProductListingsInfo as Partial<
                                  GetProjectNuggetResponse
                              >)?.getProjectNugget?.id
                            : (projectProductTypeProductListingsInfo as Partial<
                                  GetNuggetResponse
                              >)?.nugget?.id) || nugget?.id
                }
            }

            return { nodeId, projectNuggetId }
        }

        // Fetch documentTemplateViewContentItem based on isFromWorkSpace flag
        const documentTemplateViewContentItem = getDocumentTemplateViewContentItem(
            template as TemplateType<T>
        )

        // Fetch nodeId and projectNuggetId
        const { nodeId, projectNuggetId } = getNodeAndNuggetIds(
            template as TemplateType<T>,
            selectedProjectNode,
            familyAddedProductTypes,
            nugget,
            projectProductTypeProductListingsInfo,
            isFamily
        )

        // Prepare inputs for handleInActivateProperty
        const input: Partial<SetProjectContentItemIsHiddenInput> = {
            documentTemplateViewId: isFromWorkSpace
                ? dtv?.id
                : (template as NormalizedDocumentTemplateViewItem)
                      ?.documentTemplateView?.id!,
            isHidden:
                !!isOMWorkspace ||
                documentTemplateViewContentItem?.isDeactivated
                    ? false
                    : !documentTemplateViewContentItem?.isHidden,
            contentItemOwnerId: isFromWorkSpace
                ? ((template as Partial<DocumentTemplateViewItem>)
                      ?.itemId as string)
                : hasTitle
                ? ((template as NormalizedDocumentTemplateViewItem)
                      ?.itemId as string)
                : (template as NormalizedDocumentTemplateViewItem)
                      ?.documentTemplateViewItem?.itemId,
            projectId,
            nodeId: nodeId as string,
            contentItemId: isFromWorkSpace
                ? documentTemplateViewContentItem?.id
                : isFamily
                ? documentTemplateViewContentItem?.id
                    ? documentTemplateViewContentItem?.id!
                    : (template as NormalizedDocumentTemplateViewItem)
                          ?.contentItem?.id!
                : (template as NormalizedDocumentTemplateViewItem)?.contentItem
                      ?.id,
            projectNuggetId,
            isContentDeactivatedInOM:
                !!isOMWorkspace ||
                documentTemplateViewContentItem?.isDeactivated
                    ? !documentTemplateViewContentItem?.isDeactivated
                    : undefined,
        }

        // Execute callback with inputs
        handleInActivateProperty?.(input)
    } catch (_error) {
        return
    }
}

export const getSortedList = (
    list: any[],
    key: string,
    desc: boolean = false
) => {
    return [...(list ?? [])]?.sort((a, b) =>
        desc ? (b[key] ?? 0) - (a[key] ?? 0) : (a[key] ?? 0) - (b[key] ?? 0)
    )
}

export const batchUpdate = (updateFn: () => void) => {
    // Batch updates to minimize re-renders or state updates
    updateFn()
}
export const getUpdatedSkipCountForProjects = (
    skipCount: number,
    remainingProjectsToFetch: number
) => {
    // if draf masterspec project is included in projects skip count will be less than 25
    const isDraftProjectIncluded = skipCount % defaultPageSize !== 0

    const updatedSkipCount = (skipCount ?? 0) + defaultPageSize
    // if draf masterspec included and the projects to fetch is not in last batch , adjust skip count
    return isDraftProjectIncluded && remainingProjectsToFetch > 25
        ? updatedSkipCount + 1
        : updatedSkipCount
}

export const sortProjectElementsNodes = (
    currentElements: ProjectElementNode[],
    newElements: ProjectElementNode[]
): ProjectElementNode[] => {
    if (!currentElements?.length || !newElements?.length) {
        return newElements || []
    }

    const orderMap = new Map<string, number>(
        currentElements.map((item, index) => [item.treePath, index])
    )

    return [...newElements].sort((a, b) => {
        const indexA = orderMap.get(a.treePath) ?? Number.MAX_SAFE_INTEGER
        const indexB = orderMap.get(b.treePath) ?? Number.MAX_SAFE_INTEGER

        if (indexA === indexB) {
            return a.treePath.localeCompare(b.treePath)
        }

        return indexA - indexB
    })
}

/**
 * Processes service worker data for manufacturer content item updates.
 *
 * IMPORTANT: `retrieveUpdatedNugget` is called whenever `contentItems` is present
 * as an array — including an **empty array**. An empty array signals that the last
 * listing was deleted and the workspace still needs to be refreshed. Callers must
 * not pass an empty `contentItems` array unless a deletion has actually occurred.
 */
export function processManufacturerServiceWorkerData(
    data: Record<string, any> | null,
    isProductTypeElement: boolean,
    retrieveUpdatedNugget: (
        isDTVToBeRetrieved: boolean,
        isDrawingTagUpdated?: boolean,
        isSelectedFromListingDialog?: boolean
    ) => void,
    handleContentItemUpdate: (params: HandleContentItemUpdateParams) => void,
    expandedArticlesList?: string[],
    manufacturerArticleIds?: string[]
): void {
    if (!data) return

    const workspaceManufacturerContentItemsState = data[
        WORKSPACE_MANUFACTURERS_CONTENT_ITEMS_KEY
    ] as WorkspaceManufacturerContentItems

    const manufacturerListingItemsKeyListingId =
        data[WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY]?.listingId

    // True when a mutation sent a content-items update (add or delete),
    // regardless of whether the returned array is empty. An empty array
    // means the last listing was deleted and the workspace still needs
    // to be refreshed.
    const hasContentItemsMutation =
        workspaceManufacturerContentItemsState &&
        workspaceManufacturerContentItemsState.listingId &&
        Array.isArray(
            workspaceManufacturerContentItemsState.contentItems
        ) &&
        isProductTypeElement

    // True only when there are remaining items to display immediately
    // (optimistic update via handleContentItemUpdate).
    const shouldUpdateManufacturerContentItems =
        hasContentItemsMutation &&
        workspaceManufacturerContentItemsState!.contentItems!.length > 0

    if (hasContentItemsMutation || manufacturerListingItemsKeyListingId) {
        retrieveUpdatedNugget(true, undefined, true)
    }

    if (shouldUpdateManufacturerContentItems) {
        if (
            expandedArticlesList?.some((articleId) =>
                manufacturerArticleIds?.includes(articleId)
            ) ||
            manufacturerArticleIds === undefined
        ) {
            handleContentItemUpdate({
                updatedContentItems:
                    workspaceManufacturerContentItemsState!.contentItems!,
            })
        }
    }
}
